import { useTranslation } from "react-i18next";

import { useValueFormatter } from "@/utils/useValueFormatter";
import { resolveNumberLocale } from "@/utils/valueFormat";

const COMPACT_FROM = 10_000;

/** Headline amounts stay exact up to here, so a normal balance never reads as `18,5K`. */
export const HEADLINE_COMPACT_FROM = 1_000_000;

const COMPACT_UNITS = ["thousand", "million", "billion"] as const;

export function useTokenFormat() {
  const { format } = useValueFormatter();
  return (tokens: number) => format(tokens, "decimal");
}

/**
 * For tight spots: large amounts go compact (`18,5K`), so pair it with `useTokenFormat` in a `title`.
 * The digits follow the pinned number locale; the suffix follows the interface language, since
 * Intl's own compact suffixes are words (`mln.`) that would read as Dutch in every language.
 */
export function useShortTokenFormat(compactFrom = COMPACT_FROM) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const oneDecimal = new Intl.NumberFormat(resolveNumberLocale(), { maximumFractionDigits: 1 });
  return (tokens: number) => {
    if (Math.abs(tokens) < compactFrom) return format(tokens, "decimal");
    const power = compactPower(tokens);
    return t(`captainBilling.compact.${COMPACT_UNITS[power - 1]}`, {
      value: oneDecimal.format(truncateToCompact(tokens) / 1000 ** power),
    });
  };
}

const compactPower = (tokens: number) =>
  Math.min(Math.max(Math.floor(Math.log10(Math.abs(tokens)) / 3), 1), COMPACT_UNITS.length);

/**
 * Drops the digits a 1-decimal compact form would round away, so it never rounds a balance up:
 * 999.999.999 reads `999,9M`, not `1B`.
 */
export function truncateToCompact(tokens: number) {
  const step = 1000 ** compactPower(tokens) / 10;
  return Math.trunc(tokens / step) * step;
}
